/**
 * Active liveness challenge component.
 *
 * Orchestrates a multi-phase face liveness verification flow:
 * 1. Connecting -- fetch challenge token from the API
 * 2. Loading FaceMesh -- wait for the ML model to initialise
 * 3. Flashmark sequence -- passive liveness via screen illumination colours
 *    (skipped entirely when `prefers-reduced-motion: reduce` is active or
 *    when the user manually presses the skip button)
 * 4. Task challenges -- active liveness via head-pose/blink/smile detection
 * 5. Complete -- all tasks passed, deliver keyframes and selfie to parent
 *
 * Renders as a full-size overlay on top of the camera preview. The parent
 * component is expected to provide the camera's `<video>` ref via props.
 *
 * WCAG 2.2 / EAA compliance:
 * - Respects `prefers-reduced-motion: reduce` (skips flash, slower transitions)
 * - Uses `aria-live="assertive"` for task instruction changes
 * - Provides a visible "Skip" button for the flash sequence
 * - Replaces glow animation with text change when reduced motion is preferred
 *
 * @module components/LivenessChallenge
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { applicant } from '@noxverify/api-contracts';
import { useFaceMesh } from '../hooks/useFaceMesh';
import { getApiClient } from '../lib/api';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface LivenessChallengeResult {
  challengeToken: string;
  keyframes: Array<{ taskCode: string; imageBase64: string }>;
  selfieBlob: Blob;
  /** SHA-256 hash chain of captured frames for stream integrity verification (ISO 30107-3). */
  frameHashChain: string[];
}

export interface LivenessChallengeProps {
  caseId: string;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onComplete: (result: LivenessChallengeResult) => void;
  onError: (error: string) => void;
}

// ---------------------------------------------------------------------------
// Phase state machine
// ---------------------------------------------------------------------------

type Phase =
  | { type: 'connecting' }
  | { type: 'loading_facemesh' }
  | { type: 'flash_sequence' }
  | { type: 'task'; taskIndex: number; verifying: boolean }
  | { type: 'timeout' }
  | { type: 'complete' };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FLASH_CSS_COLORS: Record<applicant.FlashColor, string> = {
  red: '#ef4444',
  green: '#22c55e',
  blue: '#3b82f6',
  yellow: '#eab308',
  white: '#ffffff',
  cyan: '#00ffff',
  magenta: '#ff00ff',
};

/** Default per-task timeout in milliseconds. */
const DEFAULT_TASK_TIMEOUT_MS = 4000;

/** Duration to show "Verifying..." between tasks (anti-replay). */
const VERIFY_DELAY_MS = 1500;

/** Flash show duration before keyframe capture. */
const FLASH_SHOW_MS = 250;

/** Pause between flash colours. */
const FLASH_GAP_MS = 350;

/** Slower transition duration for reduced-motion preference. */
const REDUCED_MOTION_TRANSITION_MS = 500;

// ---------------------------------------------------------------------------
// Reduced motion detection hook
// ---------------------------------------------------------------------------

/**
 * Detect whether the user prefers reduced motion. Returns true when
 * `(prefers-reduced-motion: reduce)` matches, re-evaluates on change.
 */
function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');

    function onChange(event: MediaQueryListEvent) {
      setPrefersReduced(event.matches);
    }

    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return prefersReduced;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Draw the current video frame onto an off-screen canvas and return the image
 * data as a base64 JPEG string (without the `data:image/jpeg;base64,` prefix).
 */
function captureFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): string | null {
  if (video.readyState < 2) return null;

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
  // Strip the data URI prefix to get raw base64.
  return dataUrl.replace(/^data:image\/\w+;base64,/, '');
}

/**
 * Draw the current video frame onto an off-screen canvas and return a JPEG
 * Blob via `canvas.toBlob`.
 */
function captureBlob(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): Promise<Blob | null> {
  if (video.readyState < 2) return Promise.resolve(null);

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(null);

  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      'image/jpeg',
      0.90,
    );
  });
}

/**
 * Compute a SHA-256 hash of a frame's base64 data chained with the previous
 * hash. This creates a tamper-evident hash chain per ISO 30107-3: any
 * modification to a frame or reordering of frames invalidates the chain.
 *
 * The first frame uses an empty string as the previous hash.
 */
async function computeFrameHash(
  frameBase64: string,
  previousHash: string,
): Promise<string> {
  const data = new TextEncoder().encode(frameBase64 + previousHash);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Evaluate whether the current FaceMesh reading satisfies a task's
 * completion criteria.
 */
function isTaskSatisfied(
  task: applicant.LivenessTask,
  headPose: { yaw: number; pitch: number } | null,
  earScore: number,
  marScore: number,
): boolean {
  if (!headPose) return false;

  switch (task) {
    case 'look_straight':
      // yaw is the ratio distLeft / distRight; ~1.0 when centred.
      // The reference checks Math.abs(distLeft - distRight) < 0.08.
      // Since yaw = distLeft / distRight, we check |yaw - 1| < 0.08 for symmetry.
      return Math.abs(headPose.yaw - 1) < 0.08;
    case 'blink':
      return earScore < 0.21;
    case 'smile':
      return marScore > 0.35;
    case 'turn_right':
      // yaw > 1.4 means distLeft > distRight * 1.4 (turned right).
      return headPose.yaw > 1.4;
    case 'turn_left':
      // yaw < 1/1.4 means distRight > distLeft * 1.4 (turned left).
      return headPose.yaw < 1 / 1.4;
    case 'nod_up':
      return headPose.pitch < 0.70;
    case 'nod_down':
      return headPose.pitch > 1.40;
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Oval border colour per step index
// ---------------------------------------------------------------------------

function getOvalBorderClass(phase: Phase): string {
  if (phase.type === 'complete') {
    return 'border-primary-light border-solid';
  }
  if (phase.type === 'timeout') {
    return 'border-danger/50 border-dashed';
  }
  if (phase.type === 'task') {
    switch (phase.taskIndex) {
      case 0:
        return 'border-white/50 border-dashed';
      case 1:
        return 'border-primary-light border-dashed';
      case 2:
        return 'border-primary border-dashed';
      default:
        return 'border-primary-light border-dashed';
    }
  }
  return 'border-white/50 border-dashed';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LivenessChallenge({
  caseId,
  videoRef,
  onComplete,
  onError,
}: LivenessChallengeProps) {
  const { t } = useTranslation('portal');
  // Reduced motion preference.
  const prefersReducedMotion = usePrefersReducedMotion();

  // Phase state machine.
  const [phase, setPhase] = useState<Phase>({ type: 'connecting' });

  // Challenge data from the server.
  const challengeRef = useRef<applicant.LivenessChallengeData | null>(null);

  // Accumulated keyframes from flash + task phases.
  const keyframesRef = useRef<Array<{ taskCode: string; imageBase64: string }>>([]);

  // SHA-256 hash chain for stream integrity verification (ISO 30107-3).
  // Each entry is the SHA-256 of (frameBase64 + previousHash), creating a
  // tamper-evident chain. The queue promise serialises async hash computation
  // so that frame capture in synchronous callbacks does not race.
  const hashChainRef = useRef<string[]>([]);
  const hashChainQueueRef = useRef<Promise<void>>(Promise.resolve());

  // Off-screen canvas for frame capture.
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Refs for occlusion display.
  const [isOccludedDisplay, setIsOccludedDisplay] = useState(false);
  const [occlusionText, setOcclusionText] = useState<string | null>(null);

  // Track whether we should show the glow effect on task completion.
  const [showGlow, setShowGlow] = useState(false);

  // Active flash colour CSS value for the overlay (null = not flashing).
  const [activeFlashColor, setActiveFlashColor] = useState<string | null>(null);

  // Whether the user has manually requested to skip the flash sequence.
  const [flashSkipRequested, setFlashSkipRequested] = useState(false);

  // FaceMesh is enabled once we have the challenge data.
  const faceMeshEnabled =
    phase.type === 'loading_facemesh' ||
    phase.type === 'flash_sequence' ||
    phase.type === 'task' ||
    phase.type === 'complete';

  const faceMesh = useFaceMesh(videoRef, faceMeshEnabled);

  // Transition duration: slower for reduced motion users.
  const transitionDuration = prefersReducedMotion
    ? `${REDUCED_MOTION_TRANSITION_MS}ms`
    : '300ms';

  // -------------------------------------------------------------------------
  // Frame hash chain helper
  //
  // Enqueues an async SHA-256 hash computation so that synchronous capture
  // callbacks (inside setTimeout) can safely append to the chain without
  // awaiting. Each hash chains the previous one, creating a tamper-evident
  // sequence.
  // -------------------------------------------------------------------------

  const enqueueFrameHash = useCallback((frameBase64: string) => {
    hashChainQueueRef.current = hashChainQueueRef.current.then(async () => {
      const chain = hashChainRef.current;
      const previousHash = chain.length > 0 ? chain[chain.length - 1] : '';
      const hash = await computeFrameHash(frameBase64, previousHash);
      chain.push(hash);
    });
  }, []);

  // -------------------------------------------------------------------------
  // Phase 1: Fetch challenge token from the API
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (phase.type !== 'connecting') return;

    let cancelled = false;

    async function fetchChallenge() {
      try {
        const client = getApiClient();
        const resp = await client.applicant.requestLivenessChallenge(caseId);
        if (cancelled) return;
        challengeRef.current = resp.data;
        keyframesRef.current = [];
        hashChainRef.current = [];
        hashChainQueueRef.current = Promise.resolve();
        setPhase({ type: 'loading_facemesh' });
      } catch (err: unknown) {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : t('liveness.errors.connectFailed');
        onError(message);
      }
    }

    fetchChallenge();
    return () => { cancelled = true; };
  }, [phase.type, caseId, onError, t]);

  // -------------------------------------------------------------------------
  // Phase 2: Wait for FaceMesh to be ready
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (phase.type !== 'loading_facemesh') return;
    if (faceMesh.error) {
      onError(faceMesh.error);
      return;
    }
    if (faceMesh.isReady) {
      setPhase({ type: 'flash_sequence' });
    }
  }, [phase.type, faceMesh.isReady, faceMesh.error, onError]);

  // -------------------------------------------------------------------------
  // Phase 3: Flashmark colour sequence
  //
  // When reduced motion is preferred OR the user manually skips, jump
  // directly to the task phase without running any flash colours.
  //
  // Otherwise runs the entire colour sequence as a single chain of
  // scheduled timers. Each colour: show overlay -> wait FLASH_SHOW_MS ->
  // capture keyframe + hide overlay -> wait FLASH_GAP_MS -> next colour.
  // When all colours are done, transition to the task phase.
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (phase.type !== 'flash_sequence') return;

    const challenge = challengeRef.current;
    if (!challenge) return;

    const { flashColors } = challenge;

    // Skip flash entirely when reduced motion is preferred or user skipped.
    if (prefersReducedMotion || flashSkipRequested) {
      setActiveFlashColor(null);
      setPhase({ type: 'task', taskIndex: 0, verifying: false });
      return;
    }

    // If there are no flash colours, skip directly to tasks.
    if (!flashColors || flashColors.length === 0) {
      setPhase({ type: 'task', taskIndex: 0, verifying: false });
      return;
    }

    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    // Build a sequential timer chain for all flash colours.
    let cumulativeDelay = 0;

    for (let i = 0; i < flashColors.length; i++) {
      const color = flashColors[i];
      const cssColor = FLASH_CSS_COLORS[color] ?? '#ffffff';

      // Show flash.
      const showDelay = cumulativeDelay;
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          setActiveFlashColor(cssColor);
        }, showDelay),
      );

      // After FLASH_SHOW_MS: capture keyframe then hide flash.
      const captureDelay = cumulativeDelay + FLASH_SHOW_MS;
      timers.push(
        setTimeout(() => {
          if (cancelled) return;

          const video = videoRef.current;
          const canvas = canvasRef.current;
          if (video && canvas) {
            const base64 = captureFrame(video, canvas);
            if (base64) {
              keyframesRef.current.push({
                taskCode: `flash_${color}`,
                imageBase64: base64,
              });
              enqueueFrameHash(base64);
            }
          }

          // Hide the flash overlay.
          setActiveFlashColor(null);
        }, captureDelay),
      );

      cumulativeDelay = captureDelay + FLASH_GAP_MS;
    }

    // After all flashes complete, transition to the task phase.
    timers.push(
      setTimeout(() => {
        if (cancelled) return;
        setActiveFlashColor(null);
        setPhase({ type: 'task', taskIndex: 0, verifying: false });
      }, cumulativeDelay),
    );

    return () => {
      cancelled = true;
      for (const t of timers) clearTimeout(t);
      setActiveFlashColor(null);
    };
  }, [phase.type, videoRef, prefersReducedMotion, flashSkipRequested, enqueueFrameHash]);

  // -------------------------------------------------------------------------
  // Handle manual skip of flash sequence
  // -------------------------------------------------------------------------

  const handleSkipFlash = useCallback(() => {
    setFlashSkipRequested(true);
    // The effect above will react to this change and skip.
    if (phase.type === 'flash_sequence') {
      setActiveFlashColor(null);
      setPhase({ type: 'task', taskIndex: 0, verifying: false });
    }
  }, [phase.type]);

  // -------------------------------------------------------------------------
  // Phase 4: Task challenges -- detect completion via FaceMesh
  // -------------------------------------------------------------------------

  // Task timeout tracking.
  const taskStartRef = useRef<number>(0);

  // When entering a new task, record the start time.
  useEffect(() => {
    if (phase.type === 'task' && !phase.verifying) {
      taskStartRef.current = Date.now();
    }
  }, [phase]);

  // Monitor FaceMesh for task completion.
  useEffect(() => {
    if (phase.type !== 'task') return;
    if (phase.verifying) return;

    const challenge = challengeRef.current;
    if (!challenge) return;

    const { challenges, taskTimeoutMs } = challenge;
    const timeout = taskTimeoutMs || DEFAULT_TASK_TIMEOUT_MS;

    if (phase.taskIndex >= challenges.length) {
      // All tasks done -- complete.
      finishSequence();
      return;
    }

    const currentTask = challenges[phase.taskIndex];

    // Handle occlusion display.
    if (faceMesh.isOccluded) {
      setIsOccludedDisplay(true);
      setOcclusionText(faceMesh.occlusionMessage);
      return;
    }
    setIsOccludedDisplay(false);
    setOcclusionText(null);

    // Check timeout.
    const elapsed = Date.now() - taskStartRef.current;
    if (elapsed >= timeout) {
      setPhase({ type: 'timeout' });
      return;
    }

    // Evaluate task satisfaction.
    const satisfied = isTaskSatisfied(
      currentTask,
      faceMesh.headPose,
      faceMesh.earScore,
      faceMesh.marScore,
    );

    if (satisfied) {
      // Capture keyframe for this task.
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas) {
        const base64 = captureFrame(video, canvas);
        if (base64) {
          keyframesRef.current.push({
            taskCode: currentTask,
            imageBase64: base64,
          });
          enqueueFrameHash(base64);
        }
      }

      // Enter "Verifying..." state.
      if (!prefersReducedMotion) {
        setShowGlow(true);
      }
      setPhase({ type: 'task', taskIndex: phase.taskIndex, verifying: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phase,
    faceMesh.headPose,
    faceMesh.earScore,
    faceMesh.marScore,
    faceMesh.isOccluded,
    faceMesh.occlusionMessage,
    videoRef,
    prefersReducedMotion,
  ]);

  // When in "verifying" state, wait VERIFY_DELAY_MS then advance.
  useEffect(() => {
    if (phase.type !== 'task' || !phase.verifying) return;

    const timer = setTimeout(() => {
      setShowGlow(false);
      const challenge = challengeRef.current;
      if (!challenge) return;

      const nextIndex = phase.taskIndex + 1;
      if (nextIndex >= challenge.challenges.length) {
        finishSequence();
      } else {
        setPhase({ type: 'task', taskIndex: nextIndex, verifying: false });
      }
    }, VERIFY_DELAY_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // -------------------------------------------------------------------------
  // Phase 5: Complete
  // -------------------------------------------------------------------------

  const finishSequence = useCallback(async () => {
    setPhase({ type: 'complete' });

    // Capture the final selfie frame.
    const video = videoRef.current;
    const canvas = canvasRef.current;
    let selfieBlob: Blob | null = null;

    if (video && canvas) {
      selfieBlob = await captureBlob(video, canvas);
    }

    if (!selfieBlob) {
      onError(t('liveness.errors.selfieCapture'));
      return;
    }

    const challenge = challengeRef.current;
    if (!challenge) {
      onError(t('liveness.errors.challengeMissing'));
      return;
    }

    // Wait for any in-flight hash computations to complete before delivering
    // the result. The queue is a promise chain that serialises async work
    // enqueued from synchronous setTimeout callbacks.
    await hashChainQueueRef.current;

    // Deliver result after a short delay so the user sees the success state.
    setTimeout(() => {
      onComplete({
        challengeToken: challenge.challengeToken,
        keyframes: keyframesRef.current,
        selfieBlob: selfieBlob!,
        frameHashChain: hashChainRef.current,
      });
    }, VERIFY_DELAY_MS);
  }, [videoRef, onComplete, onError, t]);

  // -------------------------------------------------------------------------
  // Retry handler for timeout
  // -------------------------------------------------------------------------

  const handleRetry = useCallback(() => {
    keyframesRef.current = [];
    hashChainRef.current = [];
    hashChainQueueRef.current = Promise.resolve();
    challengeRef.current = null;
    setIsOccludedDisplay(false);
    setOcclusionText(null);
    setShowGlow(false);
    setActiveFlashColor(null);
    setFlashSkipRequested(false);
    setPhase({ type: 'connecting' });
  }, []);

  // -------------------------------------------------------------------------
  // Timeout check polling (runs on an interval when in task phase)
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (phase.type !== 'task' || phase.verifying) return;

    const challenge = challengeRef.current;
    if (!challenge) return;
    const timeout = challenge.taskTimeoutMs || DEFAULT_TASK_TIMEOUT_MS;

    const interval = setInterval(() => {
      const elapsed = Date.now() - taskStartRef.current;
      if (elapsed >= timeout) {
        setPhase({ type: 'timeout' });
      }
    }, 100);

    return () => clearInterval(interval);
  }, [phase]);

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const challenge = challengeRef.current;
  const ovalBorderClass = getOvalBorderClass(phase);

  const isOccludedNow =
    isOccludedDisplay && phase.type === 'task' && !phase.verifying;

  // Current instruction text.
  let instructionText = '';
  if (phase.type === 'connecting') {
    instructionText = t('liveness.instructions.connecting');
  } else if (phase.type === 'loading_facemesh') {
    instructionText = t('liveness.instructions.loading');
  } else if (phase.type === 'flash_sequence') {
    instructionText = t('liveness.instructions.lookAtScreen');
  } else if (phase.type === 'task' && challenge) {
    if (phase.verifying) {
      instructionText = prefersReducedMotion
        ? t('liveness.instructions.stepComplete')
        : t('liveness.instructions.verifying');
    } else if (isOccludedNow && occlusionText) {
      instructionText = occlusionText;
    } else {
      const task = challenge.challenges[phase.taskIndex];
      instructionText = t(`liveness.tasks.${task}`, {
        defaultValue: t('liveness.instructions.getReady'),
      });
    }
  } else if (phase.type === 'timeout') {
    instructionText = t('liveness.instructions.timeout');
  } else if (phase.type === 'complete') {
    instructionText = t('liveness.instructions.verified');
  }

  // Progress text.
  let progressText = '';
  if (phase.type === 'task' && challenge) {
    progressText = t('liveness.progress', {
      current: phase.taskIndex + 1,
      total: challenge.challenges.length,
    });
  } else if (phase.type === 'complete' && challenge) {
    progressText = t('liveness.progress', {
      current: challenge.challenges.length,
      total: challenge.challenges.length,
    });
  }

  // Flash overlay state -- never show flash overlay when reduced motion.
  const showFlash = activeFlashColor !== null && !prefersReducedMotion;

  // Determine box-shadow for the oval guide.
  const ovalBoxShadow = [
    '0 0 0 9999px rgba(0, 0, 0, 0.35)',
    phase.type === 'complete' && !prefersReducedMotion
      ? '0 0 32px rgba(52, 211, 153, 0.6)'
      : showGlow && !prefersReducedMotion
        ? '0 0 24px rgba(255, 255, 255, 0.8)'
        : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      {/* Off-screen canvas for frame capture */}
      <canvas ref={canvasRef} className="hidden" aria-hidden="true" />

      {/* Full overlay container positioned over the camera preview */}
      <div
        className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        aria-label={t('liveness.ariaLabel')}
      >
        {/* Flash colour overlay -- hidden for reduced motion users */}
        {!prefersReducedMotion && (
          <div
            className="absolute inset-0 transition-opacity duration-150"
            style={{
              backgroundColor: activeFlashColor ?? 'transparent',
              opacity: showFlash ? 0.9 : 0,
              pointerEvents: 'none',
            }}
            aria-hidden="true"
          />
        )}

        {/* Face oval guide */}
        <div
          className={[
            'relative h-[65%] w-[55%] rounded-[50%] border-[3px]',
            ovalBorderClass,
            isOccludedNow ? '!border-danger/50' : '',
          ].join(' ')}
          style={{
            boxShadow: ovalBoxShadow,
            transitionProperty: 'all',
            transitionDuration: transitionDuration,
          }}
          aria-hidden="true"
        >
          {/* Success indicator inside the oval */}
          {phase.type === 'complete' && (
            <div className="absolute inset-0 flex items-center justify-center">
              {prefersReducedMotion ? (
                /* Static checkmark without animation for reduced motion */
                <svg
                  className="h-20 w-20 text-primary-light"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg
                  className="h-20 w-20 text-primary-light animate-[scaleIn_0.3s_ease-out]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
          )}
        </div>

        {/* Instruction box below the oval */}
        <div className="mt-4 w-[80%] max-w-xs text-center">
          {/* Spinner for connecting / loading states */}
          {(phase.type === 'connecting' || phase.type === 'loading_facemesh') && (
            <div
              className="mx-auto mb-2 h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"
              aria-hidden="true"
            />
          )}

          <p
            className={[
              'text-sm font-medium',
              phase.type === 'timeout'
                ? 'text-danger'
                : phase.type === 'complete'
                  ? 'text-primary-light'
                  : isOccludedNow
                    ? 'text-danger'
                    : 'text-white',
            ].join(' ')}
            style={{
              transitionProperty: 'color',
              transitionDuration: transitionDuration,
            }}
          >
            {instructionText}
          </p>

          {/* Step progress */}
          {progressText && (
            <p className="mt-1 text-xs text-white/60">{progressText}</p>
          )}
        </div>

        {/* Skip flash button -- visible during flash sequence */}
        {phase.type === 'flash_sequence' && !prefersReducedMotion && (
          <button
            type="button"
            onClick={handleSkipFlash}
            className="pointer-events-auto mt-4 inline-flex h-9 items-center gap-1.5 rounded-lg bg-white/15 px-4 text-xs font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label={t('liveness.skipFlash')}
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            {t('liveness.skipFlash')}
          </button>
        )}

        {/* Retry button for timeout */}
        {phase.type === 'timeout' && (
          <button
            type="button"
            onClick={handleRetry}
            className="pointer-events-auto mt-4 inline-flex h-10 items-center gap-2 rounded-lg bg-white/15 px-5 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182"
              />
            </svg>
            {t('liveness.tryAgain')}
          </button>
        )}
      </div>

      {/* Inline keyframe animation for the success checkmark */}
      <style>{`
        @keyframes scaleIn {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </>
  );
}
